'use client';

import type { OverheadAndProjectResponse } from '@/app/models/overhead';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import DataTable, { getBodyCellSx } from '@/components/common/DataTable';
import { TableCell } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Column } from '../common/ExpandableDataTable';

type EventTableParams = {
  header?: OverheadAndProjectResponse;
};

export default function OverheadHeadTable(props: EventTableParams) {
  const { header } = props;
  const { t } = useTranslation(['overhead', 'common']);

  const columns: Column[] = [
    { key: 'project', label: t('project'), width: '720px' },
    { key: 'payroll', label: `${t('payroll')}` },
    { key: 'toPay', label: `${t('to_pay')}` },
    { key: 'balance', label: `${t('balance')}` },
    { key: 'priority', label: t('priority'), align: 'center' },
  ];

  const renderRow = (row: OverheadAndProjectResponse) => {
    // Use the getBodyCellSx function with invertHeaderColors=true
    const cellSx = getBodyCellSx(true);

    return (
      <>
        <TableCell sx={cellSx}>
          <TruncatedTextWithTooltip
            value={`${row.project.code} - ${row.project.name} - ${row.project.branchName}`}
            size={80}
            sx={cellSx}
          />
        </TableCell>
        <TableCell sx={cellSx}>{`${formatterMonetaryValue(row.totalValue)}`}</TableCell>
        <TableCell sx={cellSx}>{`${formatterMonetaryValue(row.overheadValue)}`}</TableCell>
        <TableCell sx={cellSx}>{`${formatterMonetaryValue(row.balanceValue)}`}</TableCell>
        <TableCell sx={{ ...cellSx, textAlign: 'center' }}>{`${row.priority}`}</TableCell>
      </>
    );
  };

  // Use the row ID as the unique key
  const getRowKey = (row: OverheadAndProjectResponse) => row.id;

  // Convert single header to array format for DataTable
  const data = header ? [header] : [];

  return (
    <DataTable
      columns={columns}
      data={data}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage=""
      maxHeight="40rem"
      stickyHeader={false}
      size="small"
      invertHeaderColors // Use the new prop to swap header and row colors
    />
  );
}
